import React, { useEffect, useRef } from 'react';

/**
 * Custom red cursor — tiny dot that tracks the OS cursor 1:1.
 *
 * (The large red ring was removed per design request — only the
 * compact red dot remains, sitting precisely on the pointer.)
 *
 * Performance characteristics:
 *   • Pure transform-driven (translate3d) — composited on GPU,
 *     never triggers layout reflow or paint.
 *   • Dot updates synchronously inside the `pointermove` handler — true
 *     1:1 with the OS cursor, zero visible lag.
 *   • CSS has NO `transition: transform` (would double-interpolate
 *     against the JS transform and make the cursor visibly trail).
 *   • The centring offset is baked into CSS `margin` instead of being
 *     concatenated into every transform string — saves a matrix
 *     multiply per frame.
 *   • Skipped entirely on touch/coarse-pointer devices and when the
 *     user has `prefers-reduced-motion: reduce`.
 */
export default function CustomCursor() {
  const dotRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.matchMedia('(pointer: coarse)').matches) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const dot = dotRef.current;
    if (!dot) return undefined;

    const onMove = (e) => {
      // Dot is 1:1 — write transform inline, NO RAF, NO CSS transition.
      dot.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
    };

    // Hide the custom dot when the pointer leaves the document so it
    // doesn't appear frozen at the last known position when the OS
    // cursor is actually free at the edge of the page.
    const onLeave = () => { dot.style.opacity = '0'; };
    const onEnter = () => { dot.style.opacity = ''; };

    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('mouseleave', onLeave);
    document.addEventListener('mouseenter', onEnter);
    window.addEventListener('blur', onLeave);

    return () => {
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('mouseenter', onEnter);
      window.removeEventListener('blur', onLeave);
    };
  }, []);

  return <div ref={dotRef} className="cursor-dot" />;
}
